import React, {
  Fragment,
  useState,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useLazyQuery } from '@apollo/client';
import { GET_CONTENTFUL_ENTRIES } from 'mocks/queries';

import InfiniteScroll from 'react-infinite-scroll-component';
import {
  getCategoryBySlug,
  buildGetEntriesQuery,
  blogFields,
} from 'lib/contentful-client';

import BlogCard from './components/BlogCard';

const BlogCategory = ({ blogUrl, slug }) => {
  const [items, updateItems] = useState([]);
  const [skip, updateSkip] = useState(0);
  const [hasMorePost, updateHasMorePost] = useState(true);
  const [loadPost, updateLoadPost] = useState(false);
  const limit = 5;
  const category = useCallback(() => getCategoryBySlug(slug), [slug])();
  const queryParams = useCallback(() => {
    let { params } = buildGetEntriesQuery(category, limit, skip, null);
    params = {
      ...params,
      contentType: 'blog',
      categories: params['fields.categories[in]'],
    };

    if (params['fields.categories[in]']) delete params['fields.categories[in]'];
    return params;
  }, [category, limit, skip])();

  const [
    fetchEntries,
    { loading: loadingContentfulEntries, data: dataContentfulEntries },
  ] = useLazyQuery(GET_CONTENTFUL_ENTRIES);

  const updateResults = results => {
    if (results?.getContentfulEntries) {
      const posts = blogFields(results.getContentfulEntries, blogUrl);
      if (posts && posts.length > 0) {
        const itemList = items.concat(posts);
        updateItems(itemList);
        updateSkip(itemList.length || 0);
        updateHasMorePost(true);
      } else {
        updateHasMorePost(false);
      }
    } else {
      updateSkip(items?.length || 0);
      updateHasMorePost(false);
    }
  };

  const loadMorePost = async () => {
    fetchEntries({ variables: queryParams });
  };

  // memo
  const featuredItemsMemo = useMemo(() => {
    if (items.length > 0) {
      return [...items.slice(0, 2)];
    }
    return [];
  }, [items]);

  const blogItemsMemo = useMemo(() => {
    if (items.length > 2) {
      return [...items.slice(2, items.length)];
    }
    return [];
  }, [items]);

  useEffect(() => {
    updateItems([]);
    updateSkip(0);
    updateHasMorePost(true);
    updateLoadPost(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (loadPost) loadMorePost();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadPost]);

  useEffect(() => {
    if (!loadingContentfulEntries && dataContentfulEntries) {
      updateResults(dataContentfulEntries);
      updateLoadPost(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingContentfulEntries, dataContentfulEntries]);
  return (
    <Fragment key={`${slug} + 'blog-category-wrapper'`}>
      <Helmet>
        <title>{`${category.text} Archives | Joybird`}</title>

        <meta
          name="description"
          content="A blog about interior design, home decor inspiration and more. Express yourself through your space."
        />

        <meta property="og:title" content="Canvas — A Blog by Joybird" />

        <meta
          property="og:description"
          content="A blog about interior design, home decor inspiration and more. Express yourself through your space."
        />

        <meta property="og:type" content="website" />
      </Helmet>

      <div className="pl-5 pr-5 mx-auto my-0 md:pl-[3.333vw] md:pr-[3.333vw] mt-[74px]">
        <div className="relative [border-bottom:1px_solid_#f1f1f1] last:[border-bottom:none]">
          <InfiniteScroll
            className="!overflow-hidden"
            dataLength={items ? items.length : 0}
            next={() => {
              updateLoadPost(true);
            }}
            hasMore={hasMorePost}
            key={category.slug}
            loader={<div>Loading...</div>}
          >
            <h1 className="text-3xl font-bold pb-2">{category.text}</h1>
            <div className="flex flex-wrap box-border w-[calc(100%_+_64px)] -m-8">
              {featuredItemsMemo.map(post => (
                <div
                  key={post.slug}
                  className="mt-0 mx-0 mb-4 box-border p-8 flex-grow-0 max-w-full basis-full min-[600px]:max-w-[50%] min-[600px]:basis-1/2"
                >
                  <BlogCard post={post} />
                </div>
              ))}

              {blogItemsMemo?.length > 0
                ? blogItemsMemo.map(post => (
                    <div
                      key={post.slug}
                      className="mt-0 mx-0 mb-4 box-border p-8 flex-grow-0 max-w-full basis-full min-[600px]:max-w-[33.333%] min-[600px]:basis-1/3"
                    >
                      <BlogCard post={post} />
                    </div>
                  ))
                : null}
            </div>
          </InfiniteScroll>
        </div>
      </div>
    </Fragment>
  );
};
BlogCategory.propTypes = {
  blogUrl: PropTypes.string,
  slug: PropTypes.string,
};
export default BlogCategory;
